import { Airflow } from '../../../enums/airflow';
import {
    AirflowIndicatorText,
    AirflowLine,
    InnerSizeSelectionContainer,
    NoMatchingSizes,
    PassivHausSizeElement,
    SizeElement,
    SizeElementContainer,
    SizeHeader,
    SizeSelectionContainer,
    SizeSelectionGrid
} from './SizeSelection.styles';
import SizeSelectionLegend from './SizeSelectionLegend';
import {
    noAirflowValuesExists,
    ElementSizes,
    getElementSizes,
    getMaxAirFlow,
    getMinAirFlow
} from './sizeSelection.functions';
import Tooltip from './Tooltip';
import { Context as TranslationContext } from '../../contexts/TranslationContext';
import { useContext } from 'react';
import {
    ProductVariantSizeDto,
    ProductVariantSizesDto
} from '../../../types/ProductVariant.types';

export type SizeSelectionProps = {
    productVariantSizes: ProductVariantSizesDto | undefined;
    productVariantSizesWithinAirflowRange: string[];
    selectedProductVariantSize: string | undefined;
    onSizeSelected: (newSizeId: string) => void;
    supplyAirflowInput: number;
    extractAirflowInput: number;
    heatRecoveryType: string;
    passivhausCompatible: boolean;
    designViewMode: boolean;
    phiSelected?: boolean | undefined;
    translatedUnit: string;
};

const SizeSelection = (props: SizeSelectionProps) => {
    const {
        productVariantSizes,
        productVariantSizesWithinAirflowRange,
        selectedProductVariantSize,
        onSizeSelected,
        supplyAirflowInput,
        extractAirflowInput,
        heatRecoveryType,
        passivhausCompatible,
        designViewMode,
        phiSelected = undefined,
        translatedUnit
    } = props;
    const { l } = useContext(TranslationContext);

    const getTooltipText = (size: ProductVariantSizeDto) => {
        const min = size.supplyAirflow?.min || size.extractAirflow?.min;
        const max = size.supplyAirflow?.max || size.extractAirflow?.max;

        let text =
            `Min ${l('common.airFlow')}: ${min} ${translatedUnit}\n` +
            ` Max ${l('common.airFlow')}: ${max} ${translatedUnit}`;

        if (phiSelected && size.phiAirflow?.min && size.phiAirflow?.max) {
            text +=
                `\n Passiv Haus min ${l('common.airFlow')}: ${size.phiAirflow?.min} ${size.phiAirflow?.unit}` +
                `\n Passiv Haus max ${l('common.airFlow')}: ${size.phiAirflow?.max} ${size.phiAirflow?.unit}`;
        }

        return text;
    };
    const elementSizes = getElementSizes(designViewMode, productVariantSizes);

    const calculateAirflowlinesPositions = (elementSizes: ElementSizes) => {
        return {
            supply: {
                left: Math.min(
                    (supplyAirflowInput /
                        elementSizes.maxAirflowInProductVariantSizes) *
                        elementSizes.sizesDrawAreaWidth,
                    elementSizes.sizesDrawAreaWidth
                )
            },
            extract: {
                left: Math.min(
                    (extractAirflowInput /
                        elementSizes.maxAirflowInProductVariantSizes) *
                        elementSizes.sizesDrawAreaWidth,
                    elementSizes.sizesDrawAreaWidth
                )
            }
        };
    };

    return (
        <SizeSelectionContainer>
            {!productVariantSizes && <NoMatchingSizes />}
            <SizeSelectionGrid>
                <SizeHeader>{l('unitTypeSelector.size')}</SizeHeader>
                <InnerSizeSelectionContainer
                    key={`InnerSizeSelectionContainer`}
                >
                    <AirflowLine
                        key={`AirflowLine.${Airflow.SUPPLY}`}
                        $airflow={Airflow.SUPPLY}
                        $positionLeft={
                            calculateAirflowlinesPositions(elementSizes).supply
                                .left
                        }
                    />
                    <AirflowLine
                        key={`AirflowLine.${Airflow.EXTRACT}`}
                        $airflow={Airflow.EXTRACT}
                        $positionLeft={
                            calculateAirflowlinesPositions(elementSizes).extract
                                .left
                        }
                    />
                    {productVariantSizes?.sizes?.map(
                        (size: ProductVariantSizeDto) => {
                            if (noAirflowValuesExists(size)) {
                                return null;
                            }

                            const minAirFlow = getMinAirFlow(size);
                            const maxAirFlow = getMaxAirFlow(size);

                            const lineWidth = 3;
                            const marginLeft =
                                ((minAirFlow -
                                    elementSizes.minAirflowInProductVariantSizes) /
                                    (elementSizes.maxAirflowInProductVariantSizes -
                                        elementSizes.minAirflowInProductVariantSizes)) *
                                    elementSizes.sizesDrawAreaWidth +
                                lineWidth / 2;

                            const totalWidth =
                                (maxAirFlow /
                                    elementSizes.maxAirflowInProductVariantSizes) *
                                    elementSizes.sizesDrawAreaWidth -
                                marginLeft;

                            const isSelected =
                                selectedProductVariantSize === size.sizeId;
                            const sizeIsWithinAirflowRange =
                                productVariantSizesWithinAirflowRange?.filter(
                                    (sizeName) => sizeName === size.sizeName
                                ).length === 1;

                            const phiRange =
                                size.phiAirflow?.min || size.phiAirflow?.max;

                            const phiMarginLeft =
                                ((size.phiAirflow?.min -
                                    elementSizes.minAirflowInProductVariantSizes) /
                                    (elementSizes.maxAirflowInProductVariantSizes -
                                        elementSizes.minAirflowInProductVariantSizes)) *
                                    elementSizes.sizesDrawAreaWidth +
                                lineWidth / 2;

                            const phiTotalWidth =
                                (size.phiAirflow?.max /
                                    elementSizes.maxAirflowInProductVariantSizes) *
                                    elementSizes.sizesDrawAreaWidth -
                                phiMarginLeft -
                                2;

                            const phiHeight = elementSizes.height - 4;

                            return (
                                <SizeElementContainer
                                    key={`SizeElementContainer.${size.sizeName}`}
                                    selected={isSelected}
                                >
                                    <Tooltip
                                        key={`SizeElement.Tooltip.${size.sizeName}`}
                                        text={getTooltipText(size)}
                                        children={
                                            <>
                                                <SizeElement
                                                    role="button"
                                                    key={`SizeElement.Supply.${size.sizeName}`}
                                                    $selected={isSelected}
                                                    onClick={() =>
                                                        onSizeSelected(
                                                            size.sizeId
                                                        )
                                                    }
                                                    $inrange={
                                                        sizeIsWithinAirflowRange
                                                    }
                                                    $airflow={Airflow.SUPPLY}
                                                    $width={
                                                        !isNaN(totalWidth)
                                                            ? totalWidth
                                                            : 0
                                                    }
                                                    $height={
                                                        !isNaN(
                                                            elementSizes.height
                                                        )
                                                            ? elementSizes.height
                                                            : 0
                                                    }
                                                    $marginleft={marginLeft}
                                                >
                                                    {size.sizeName}
                                                </SizeElement>
                                                {phiRange !== undefined &&
                                                    !designViewMode &&
                                                    phiSelected && (
                                                        <PassivHausSizeElement
                                                            key={`SizeElement.Phi.${size.sizeName}`}
                                                            $selected={
                                                                isSelected
                                                            }
                                                            onClick={() =>
                                                                onSizeSelected(
                                                                    size.sizeId
                                                                )
                                                            }
                                                            $inrange={
                                                                sizeIsWithinAirflowRange
                                                            }
                                                            $airflow={
                                                                Airflow.SUPPLY
                                                            }
                                                            $width={
                                                                !isNaN(
                                                                    phiTotalWidth
                                                                )
                                                                    ? phiTotalWidth
                                                                    : 0
                                                            }
                                                            $height={
                                                                !isNaN(
                                                                    phiHeight
                                                                )
                                                                    ? phiHeight
                                                                    : 0
                                                            }
                                                            $marginleft={
                                                                !isNaN(
                                                                    phiMarginLeft
                                                                )
                                                                    ? phiMarginLeft
                                                                    : 0
                                                            }
                                                        />
                                                    )}
                                            </>
                                        }
                                    />
                                </SizeElementContainer>
                            );
                        }
                    )}
                    <AirflowIndicatorText $floatDirection={'left'}>
                        {`${elementSizes.minAirflowInProductVariantSizes} ${translatedUnit}`}
                    </AirflowIndicatorText>
                    <AirflowIndicatorText $floatDirection="right">
                        {`${elementSizes.maxAirflowInProductVariantSizes} ${translatedUnit}`}
                    </AirflowIndicatorText>
                </InnerSizeSelectionContainer>
                <SizeSelectionLegend
                    designViewMode={designViewMode}
                    heatRecoveryType={heatRecoveryType}
                    passivhausCompatible={passivhausCompatible}
                />
            </SizeSelectionGrid>
        </SizeSelectionContainer>
    );
};

export default SizeSelection;
